import React from 'react';
import classnames from 'classnames/bind';
import { Controller, useForm } from 'react-hook-form';

import { NavRoute, SettingsRoute } from 'application/types/routes';
import {
  NotificationRule,
  NotificationCategory,
  NotificationType,
  getNotificationCategoryLabel,
} from 'types/notification_rule';

import {
  useCreateNotificationRule,
  CreateNotificationRuleParams,
} from 'settings/hooks/useCreateNotificationRule';
import { useUpdateNotificationRule } from 'settings/hooks/useUpdateNotificationRule';
import { useNotificationCategories } from 'settings/hooks/useNotificationCategories';
import { useNotificationTypes } from 'settings/hooks/useNotificationTypes';
import { useUserGroups } from 'settings/hooks/useUserGroups';

import { AppLink } from 'components/AppLink/AppLink';
import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Drawer } from 'components/Drawer/DrawerDeprecated';
import { Field } from 'components/Field/Field';
import { FormErrorMessage } from 'components/FormMessage/FormErrorMessage';
import { Input } from 'components/Input/Input';
import { Select } from 'components/Select/Select';

import {
  EntityFiltersSection,
  EntityFilterFieldValues,
  ENTITY_FILTER_DEFAULT_VALUES,
  buildEntityFilters,
  parseEntityFilters,
} from './EntityFiltersSection';
import styles from './NotificationRuleForm.module.scss';

const c = classnames.bind(styles);

type SelectOption = { value: string; label: string };

type NotificationRuleFieldValues = {
  name: string;
  notification_categories: SelectOption[];
  notification_types: SelectOption[];
  user_group_ids: SelectOption[];
  title_keywords: string;
} & EntityFilterFieldValues;

const FORM_ID = 'notification-rule-form';

type NotificationRuleFormProps = {
  open: boolean;
  onClose: () => void;
  rule?: NotificationRule;
};

export function NotificationRuleForm({
  open,
  onClose,
  rule,
}: NotificationRuleFormProps) {
  const isEditing = Boolean(rule);

  const { data: categoriesData } = useNotificationCategories();
  const { data: typesData } = useNotificationTypes();
  const { data: userGroupsData } = useUserGroups({ params: { limit: 100 } });

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
    setValue,
  } = useForm<NotificationRuleFieldValues>();

  const selectedCategories = watch('notification_categories');

  React.useEffect(() => {
    if (!open) return;

    const entityFilterValues = rule
      ? parseEntityFilters(rule.entity_filters)
      : {};

    if (rule) {
      reset({
        name: rule.name,
        notification_categories: rule.notification_categories.map((c) => ({
          value: c,
          label: getNotificationCategoryLabel(c),
        })),
        notification_types:
          rule.notification_types?.map((t) => ({
            value: t,
            label: formatLabel(t),
          })) ?? [],
        user_group_ids: rule.user_group_ids.map((id) => {
          const group = userGroupsData?.data?.find((g) => g.id === id);
          return { value: id, label: group?.name ?? id };
        }),
        title_keywords: rule.title_keywords.join(', '),
        ...ENTITY_FILTER_DEFAULT_VALUES,
        ...entityFilterValues,
      });
    } else {
      reset({
        name: '',
        notification_categories: [],
        notification_types: [],
        user_group_ids: [],
        title_keywords: '',
        ...ENTITY_FILTER_DEFAULT_VALUES,
      });
    }
  }, [open, rule, reset, userGroupsData]);

  const {
    mutate: createRule,
    isLoading: isCreating,
    error: createError,
  } = useCreateNotificationRule({ onSuccess: onClose });

  const {
    mutate: updateRule,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateNotificationRule({ onSuccess: onClose });

  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  const categoryOptions: SelectOption[] = (categoriesData ?? []).map((c) => ({
    value: c,
    label: getNotificationCategoryLabel(c),
  }));

  const selectedCategoryValues = new Set(
    (selectedCategories ?? []).map((c) => c.value)
  );

  const typeOptions: SelectOption[] = (typesData ?? [])
    .filter(
      (t) =>
        selectedCategoryValues.size === 0 ||
        t.categories.some((c) => selectedCategoryValues.has(c))
    )
    .map((t) => ({ value: t.value, label: formatLabel(t.value) }));

  const userGroupOptions: SelectOption[] = (userGroupsData?.data ?? []).map(
    (g) => ({ value: g.id, label: g.name })
  );

  const showAnalyticsKeywords = selectedCategoryValues.has('analytics');

  function onSubmit(values: NotificationRuleFieldValues) {
    const params: CreateNotificationRuleParams = {
      name: values.name,
      notification_categories: values.notification_categories.map(
        (c) => c.value as NotificationCategory
      ),
      notification_types:
        values.notification_types.length > 0
          ? values.notification_types.map((t) => t.value as NotificationType)
          : null,
      user_group_ids: values.user_group_ids.map((g) => g.value),
      frequency: 'immediate',
      title_keywords: parseTitleKeywords(values.title_keywords),
      entity_filters: buildEntityFilters(values),
    };

    if (rule) {
      updateRule({ id: rule.id, ...params });
    } else {
      createRule(params);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit notification rule' : 'Create notification rule'}
      footer={
        <ButtonGroup align="end">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            loading={isLoading}
          >
            {isEditing ? 'Save' : 'Create'}
          </Button>
        </ButtonGroup>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-3"
      >
        <FormErrorMessage error={error} />

        {/* Group 1: Name, Categories & Types */}
        <Field id="rule-name" label="Name" error={errors.name} required>
          <Input
            {...register('name', { required: 'Please enter a rule name.' })}
            autoFocus
          />
        </Field>

        <Field
          label="Categories"
          error={errors.notification_categories}
          required
        >
          <Controller
            control={control}
            name="notification_categories"
            rules={{ required: 'Please select at least one category.' }}
            render={({ field: { value, onChange } }) => (
              <Select<SelectOption, true>
                isMulti
                value={value}
                onChange={(selected) => onChange([...selected])}
                options={categoryOptions}
                placeholder="Select categories..."
              />
            )}
          />
        </Field>

        <Field label="Notification types">
          <Controller
            control={control}
            name="notification_types"
            render={({ field: { value, onChange } }) => (
              <Select<SelectOption, true>
                isMulti
                value={value}
                onChange={(selected) => onChange([...selected])}
                options={typeOptions}
                placeholder="All types in selected categories"
              />
            )}
          />
        </Field>

        {showAnalyticsKeywords && (
          <Field id="rule-title-keywords" label="Title keywords">
            <Input
              {...register('title_keywords')}
              placeholder="loitering, intrusion"
            />
            <p className="mt-1 text-xs text-gray-500">
              Comma-separated. Match analytics events whose title contains any
              of these (case-insensitive). Leave empty to match all titles.
            </p>
          </Field>
        )}

        <hr className="my-1 border-t border-gray-200" />

        {/* Group 2: Entity filters */}
        <EntityFiltersSection
          control={control}
          setValue={setValue}
          initialEntityFilters={rule?.entity_filters}
        />

        <hr className="my-1 border-t border-gray-200" />

        {/* Group 3: Recipient groups */}
        <Field label="Recipient Groups" error={errors.user_group_ids} required>
          <p className="mb-2 text-xs text-gray-500">
            Select the member groups that should receive these notifications.{' '}
            <AppLink
              to={`/${NavRoute.SETTINGS}/${SettingsRoute.GROUPS}`}
              className={c('manage-groups-link')}
            >
              Manage member groups
            </AppLink>
            .
          </p>
          <Controller
            control={control}
            name="user_group_ids"
            rules={{ required: 'Please select at least one recipient group.' }}
            render={({ field: { value, onChange } }) => (
              <Select<SelectOption, true>
                isMulti
                value={value}
                onChange={(selected) => onChange([...selected])}
                options={userGroupOptions}
                placeholder="Select recipient groups..."
              />
            )}
          />
        </Field>
      </form>
    </Drawer>
  );
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseTitleKeywords(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}
